import { useState, useMemo, useEffect } from "react";
import { Modal, Button, Form, ListGroup, Spinner } from "react-bootstrap";
import { I18n } from "@/i18n/I18n";
import { useRequirementsStore } from "@/frontend/store/RequirementsStore";
import { useShallow } from "zustand/react/shallow";
import { useWorkspaceStore } from "@/frontend/store/WorkspaceStore";
import { Project, ProjectParentType } from "@/backend/models/project";
import { RequirementSubjectType } from "@/backend/access/requirement/types";

interface AssignRequirementModalProps {
  onHide: () => void;
  project: Project;
}

export function AssignRequirementModal(props: AssignRequirementModalProps) {
  const { onHide, project } = props;
  const organizationRequirements = useRequirementsStore(state => state.organizationRequirements);
  const assignedRequirements = useRequirementsStore(useShallow(state => state.requirements.filter(r => r.subjectId === project.id && r.subjectType === RequirementSubjectType.PROJECT)));
  const workspace = useWorkspaceStore(useShallow(state => state.workspaces.find(w => w.id === project.parentId)));
  const organizationId = project.parentType === ProjectParentType.ORGANIZATION ? project.parentId : workspace?.ownerFusaOrgId;
  const fetchOrganizationRequirements = useRequirementsStore(state => state.fetchOrganizationRequirements);
  const assignRequirementsToProject = useRequirementsStore(state => state.assignRequirements);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (organizationId) {
      setLoading(true);
      void fetchOrganizationRequirements(organizationId).finally(() => {
        setLoading(false);
      });
    }
  }, [fetchOrganizationRequirements, organizationId]);

  const unassignedRequirements = useMemo(() => {
    const assignedSourceIds = new Set(
      assignedRequirements
        .map(req => req.sourceId)
        .filter((sourceId): sourceId is string => sourceId != null)
    );
    return organizationRequirements.filter(req => !assignedSourceIds.has(req.id));
  }, [organizationRequirements, assignedRequirements]);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRequirementIds, setSelectedRequirementIds] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredRequirements = useMemo(() => 
    unassignedRequirements.filter(req => 
      req.name.toLowerCase().includes(searchTerm.toLowerCase())
    ), [unassignedRequirements, searchTerm]);

  const allFilteredSelected = filteredRequirements.length > 0 &&
    filteredRequirements.every(req => selectedRequirementIds.has(req.id));

  const toggleAll = () => {
    setSelectedRequirementIds(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredRequirements.forEach(req => next.delete(req.id));
      } else {
        filteredRequirements.forEach(req => next.add(req.id));
      }
      return next;
    });
  };

  const toggleRequirement = (requirementId: string) => {
    setSelectedRequirementIds(prev => {
      const next = new Set(prev);
      if (next.has(requirementId)) {
        next.delete(requirementId);
      } else {
        next.add(requirementId);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedRequirementIds.size === 0) return;
    
    setIsSubmitting(true);
    try {
      await assignRequirementsToProject(project.id, Array.from(selectedRequirementIds));
      onHide();
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onHide();
    }
  };

  return (
    <Modal show onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{I18n.get('assignRequirements')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>{I18n.get('searchRequirements')}</Form.Label>
          <Form.Control
            type="text"
            placeholder={I18n.get('searchByName')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
        </Form.Group>

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : filteredRequirements.length === 0 ? (
          <div className="text-center py-4 text-muted">
            {searchTerm ? I18n.get('noRequirementsMatchSearch') : I18n.get('allRequirementsAssigned')}
          </div>
        ) : (
          <>
            <div className="d-flex align-items-center mb-2 px-2">
              <Form.Check
                id="select-all-requirements"
                type="checkbox"
                checked={allFilteredSelected}
                onChange={toggleAll}
                label={allFilteredSelected ? I18n.get('deselectAll') : I18n.get('selectAll')}
                className="fw-semibold"
              />
            </div>
            <ListGroup style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {filteredRequirements.map((requirement) => {
                const isSelected = selectedRequirementIds.has(requirement.id);
                return (
                  <ListGroup.Item
                    key={requirement.id}
                    action
                    onClick={() => toggleRequirement(requirement.id)}
                    className="cursor-pointer"
                  >
                    <div className="d-flex align-items-start">
                      <Form.Check
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRequirement(requirement.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="me-3 mt-1"
                      />
                      <div className="flex-grow-1">
                        <h6 className="mb-1">{requirement.name}</h6>
                        {requirement.description && (
                          <p className="mb-0 small text-muted">
                            {requirement.description.length > 250
                              ? `${requirement.description.substring(0, 250)}...` 
                              : requirement.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
          {I18n.get('cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={() => void handleSubmit()}
          disabled={selectedRequirementIds.size === 0 || isSubmitting}
        >
          {isSubmitting 
            ? I18n.get('assigning') 
            : `${I18n.get('assign')} (${selectedRequirementIds.size})`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

